import React from 'react';
import { GraduationCap, BookOpen, Activity, Clock, FileText } from 'lucide-react';

/**
 * Preview Information & Education Page
 * Read-only education overview
 */
const PreviewEducationPage = () => {
  const sections = [
    {
      icon: BookOpen,
      title: 'What Is CLL',
      description: 'Understanding Chronic Lymphocytic Leukemia - basics, diagnosis, and what it means',
      color: 'blue'
    },
    {
      icon: Clock,
      title: 'Watch and Wait',
      description: 'Active surveillance approach - when and why doctors recommend monitoring',
      color: 'teal'
    },
    {
      icon: Activity,
      title: 'Blood Markers',
      description: 'Understanding your blood test results and what the numbers mean',
      color: 'purple'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: { bg: 'bg-blue-50', icon: 'text-blue-600', border: 'border-blue-200' },
      teal: { bg: 'bg-teal-50', icon: 'text-teal-600', border: 'border-teal-200' },
      purple: { bg: 'bg-purple-50', icon: 'text-purple-600', border: 'border-purple-200' }
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Information & Education</h1>
        <p className="text-slate-500 mt-1">Learn about CLL and understand your journey</p>
      </div>

      {/* Intro Card */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100 p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-white/80 rounded-xl">
            <GraduationCap className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900 mb-2">Knowledge Is Power</h2>
            <p className="text-slate-600">
              Understanding your condition helps you make informed decisions and have better 
              conversations with your healthcare team. These resources are designed to help 
              you learn at your own pace.
            </p>
          </div>
        </div>
      </div>

      {/* Section Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {sections.map((section) => {
          const Icon = section.icon;
          const colors = getColorClasses(section.color);
          
          return (
            <div
              key={section.title}
              className={`bg-white rounded-xl border ${colors.border} p-5 opacity-75 cursor-not-allowed`}
            >
              <div className={`p-3 ${colors.bg} rounded-lg w-fit mb-3`}>
                <Icon className={`h-6 w-6 ${colors.icon}`} />
              </div>
              <h3 className="font-semibold text-slate-900 mb-1">{section.title}</h3>
              <p className="text-sm text-slate-500">{section.description}</p>
              <p className="text-xs text-slate-400 mt-2">Available in full app</p>
            </div>
          );
        })}
      </div>

      {/* Additional Resources */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Why Education Matters</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-green-50 rounded-lg">
              <FileText className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="font-medium text-slate-900">Better Conversations</p>
              <p className="text-sm text-slate-500">Ask informed questions at appointments</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="p-2 bg-amber-50 rounded-lg">
              <Clock className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="font-medium text-slate-900">Reduced Anxiety</p>
              <p className="text-sm text-slate-500">Understanding reduces fear of the unknown</p>
            </div>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 text-center">
        <p className="text-sm text-slate-500">
          ⚠️ This information is educational only and does not replace professional medical advice.
        </p>
      </div>
    </div>
  );
};

export default PreviewEducationPage;
